import { useCollection, useCollections } from "@/api/hooks";
import { getCollectionChildrenRecoursively } from "@/common/utils";
import { useAppDispatch } from "@/state/redux/hooks";
import { store } from "@/state/redux/store";
import { setViewingRecord } from "@/state/redux/slices/ui";
import {
  setChildrenCollectionIDs,
  setSelectedChildrenCollectionIDs,
} from "@/state/redux/slices/collections";
import { useEffect, useMemo } from "react";

export default function CollectionsListener() {
  const dispatch = useAppDispatch();
  const collections = useCollections();
  // The URL segment is a ref -- usually a slug -- and both uses below match on
  // real ids, so reading it raw made this a no-op at best and a preview that
  // closed itself at worst.
  const collection = useCollection();
  const collectionID = collection?.id;
  // What the page is showing: the collection itself plus everything below it,
  // through both kinds of edge -- the same set the API pages records from. No
  // collection in the URL means the whole space.
  const childrenIDs = useMemo(
    () =>
      collectionID
        ? getCollectionChildrenRecoursively(collectionID, collections)
        : collections.map((c) => c.id),
    [collectionID, collections],
  );

  useEffect(() => {
    dispatch(setChildrenCollectionIDs(childrenIDs));
    dispatch(setSelectedChildrenCollectionIDs(childrenIDs));
  }, [childrenIDs, dispatch]);

  useEffect(() => {
    if (!collectionID) return;
    // Read off the store rather than subscribed to. Closing the preview is a
    // reaction to *navigating* to another collection; with the record among
    // this effect's dependencies it re-ran on the very click that opened it,
    // and shut it again before it was ever seen.
    const record = store.getState().ui.viewingRecord;
    if (!record) return;

    // A collection page lists its own records *and its descendants'*, so the
    // preview survives anywhere in that scope. Matching the collection itself
    // alone dismissed every record that lives one level down.
    const scope = new Set([collectionID, ...childrenIDs]);
    if ((record.collection_id ?? []).some((id) => scope.has(id))) return;

    // A title record is deliberately absent from the collection it heads, so it
    // is in no scope by membership -- yet the band above the timeline hands the
    // current collection's one to the preview on purpose, and a descendant's
    // one heads a row in the list.
    if (record.title_collection_id && scope.has(record.title_collection_id)) {
      return;
    }

    dispatch(setViewingRecord(null));
  }, [collectionID, childrenIDs, dispatch]);

  return null;
}
